import { useEffect, useState } from "react";
import { api } from "@/lib/rn3";
import { ShieldCheck, ShieldAlert, ShieldOff, RefreshCcw } from "lucide-react";
import { useT } from "@/lib/i18n";

/**
 * Admin-only virus-scan telemetry panel.
 * Pulls GET /admin/documents/scan-stats every 30s (paused when tab is hidden)
 * and renders live counts + a table of recent malware blocks + engine health.
 * Intended to live at the bottom of /admin next to SessionCard.
 */
const StatBox = ({ icon: Icon, tint, label, value, testid }) => (
  <div className="border border-[#E5E5E5] p-4 flex items-start gap-3" data-testid={testid}>
    <Icon className={`w-5 h-5 mt-0.5 ${tint}`} />
    <div>
      <div className="text-2xl font-serif-display text-[#0A0A0A] leading-none" data-testid={`${testid}-value`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">{label}</div>
    </div>
  </div>
);

const ScanStatusPanel = () => {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/documents/scan-stats?days=30");
      setData(d);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(() => { if (!document.hidden) load(); }, 30000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tt = (k, fb) => { const v = t(k); return v === k ? fb : v; };
  const counts = data?.counts || {};
  const engine = data?.engine_status || {};

  return (
    <section className="mt-12" data-testid="admin-scan-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-serif-display text-2xl flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#C4A252]" />
          {tt("admin.scan.heading", "Virus Scan Status")}
        </h2>
        <button
          onClick={load}
          disabled={loading}
          className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] flex items-center gap-1"
          data-testid="admin-scan-refresh"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          {tt("admin.scan.refresh", "Refresh")}
        </button>
      </div>
      <div className="border-t border-[#C4A252] w-10 mt-3 mb-5" />

      {/* Engine health strip */}
      <div className="mb-4 flex items-center gap-3 text-xs" data-testid="admin-scan-engine">
        <span className="uppercase tracking-widest text-[#737373]">{tt("admin.scan.engine", "Engine")}:</span>
        <span className="font-medium text-[#0A0A0A]">ClamAV</span>
        {engine.socket_ok ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#E7F5EC] text-[#0A7C2E] text-[10px] uppercase tracking-widest" data-testid="admin-scan-engine-status">
            <ShieldCheck className="w-3 h-3" /> {tt("admin.scan.engine_ok", "Online")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FDECEC] text-[#8B1C22] text-[10px] uppercase tracking-widest" data-testid="admin-scan-engine-status">
            <ShieldOff className="w-3 h-3" /> {tt("admin.scan.engine_offline", "Offline")}
          </span>
        )}
        {engine.sample_scan_ms != null && (
          <span className="text-[#737373]" data-testid="admin-scan-engine-latency">
            {tt("admin.scan.latency", "Latency")}: {engine.sample_scan_ms} ms
          </span>
        )}
      </div>

      {/* Counts grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatBox icon={ShieldCheck} tint="text-[#0A7C2E]"
                 label={tt("admin.scan.clean", "Scanned Clean")}
                 value={counts.clean ?? 0} testid="admin-scan-clean" />
        <StatBox icon={ShieldAlert} tint="text-[#C4A252]"
                 label={tt("admin.scan.quarantined", "In Quarantine")}
                 value={counts.quarantined ?? 0} testid="admin-scan-quarantined" />
        <StatBox icon={ShieldOff} tint="text-[#8B1C22]"
                 label={tt("admin.scan.blocked", "Blocked (30d)")}
                 value={data?.infected_last_ndays ?? 0} testid="admin-scan-blocked" />
        <StatBox icon={ShieldCheck} tint="text-[#737373]"
                 label={tt("admin.scan.total", "Total Files")}
                 value={counts.total ?? 0} testid="admin-scan-total" />
      </div>

      {/* Recent malware blocks */}
      <div className="mt-6" data-testid="admin-scan-recent">
        <h3 className="text-sm uppercase tracking-widest text-[#0A0A0A] mb-3">
          {tt("admin.scan.recent_heading", "Recent malware blocks")}
        </h3>
        {(!data?.recent_infected || data.recent_infected.length === 0) ? (
          <div className="text-sm text-[#737373] border border-dashed border-[#E5E5E5] p-4" data-testid="admin-scan-empty">
            {tt("admin.scan.recent_empty", "No malicious uploads detected. ClamAV is watching every file.")}
          </div>
        ) : (
          <div className="overflow-x-auto border border-[#E5E5E5]">
            <table className="w-full text-xs">
              <thead className="bg-[#FDFBF7] text-[#737373] uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="text-left p-2">{tt("admin.scan.col_when", "When")}</th>
                  <th className="text-left p-2">{tt("admin.scan.col_signature", "Signature")}</th>
                  <th className="text-left p-2">{tt("admin.scan.col_filename", "Filename")}</th>
                  <th className="text-left p-2">{tt("admin.scan.col_uploader", "Uploader")}</th>
                  <th className="text-left p-2">{tt("admin.scan.col_ip", "IP")}</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_infected.map((r) => (
                  <tr key={r.id} className="border-t border-[#E5E5E5]" data-testid={`admin-scan-row-${r.id}`}>
                    <td className="p-2 text-[#737373] whitespace-nowrap">
                      {r.timestamp ? new Date(r.timestamp).toLocaleString() : "—"}
                    </td>
                    <td className="p-2 font-medium text-[#8B1C22]">{r.details?.signature || "unknown"}</td>
                    <td className="p-2 text-[#0A0A0A]">{r.details?.filename || "—"}</td>
                    <td className="p-2 text-[#0A0A0A]">
                      {r.user_email || <span className="text-[#737373] italic">{tt("admin.scan.anonymous", "anonymous")}</span>}
                      {r.details?.intake_id && (
                        <span className="text-[10px] text-[#737373] ml-2">
                          intake <code>{String(r.details.intake_id).slice(0, 8)}</code>
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-[#737373]">{r.ip_address || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default ScanStatusPanel;
